import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  PUBLIC_LINK_LIMITS,
  validateSubmission,
  type AnswerValue,
  type PublicSurveyView,
} from 'shared'
import { Alert, Button, Card, EmptyState, Field, Input, SectionSpinner } from '../components/ui'
import QuestionList from '../components/survey/QuestionList'
import { withSeedRows } from '../components/survey/tableAnswers'
import { cx } from '../lib/utils'
import { api, ApiError } from '../utils/api'
import { toErrorMessage } from '../lib/errors'
import { loadTurnstile } from '../lib/turnstile'
import { useNoReferrer } from '../lib/useNoReferrer'
import { formatDate } from '../lib/dates'

/**
 * The page behind a public survey link: the one place in ScaleBrain an
 * unauthenticated person lands on purpose.
 *
 * It asks for nothing it does not need. Not anonymous: a name, said plainly to
 * be taken as declared. Anonymous: not even that. The privacy notice is shown
 * before the form and has to be accepted to send.
 */

/**
 * A cookie that says this browser already sent an answer. It is a courtesy, not a
 * defence: it clears with the browser, and another browser has none. The cap on
 * answers and the limit per address are what hold; this only stops an honest
 * person from sending twice by accident.
 */
function doneCookie(token: string): string {
  return `sb_public_done_${fingerprint(token)}`
}

/**
 * A short, one-way digest of the link for the cookie's name. The name travels in
 * every request to this origin, so it must not spell out a piece of the link: a
 * slice of it would hand over ninety-odd bits of a credential. This is not a
 * cryptographic hash and does not need to be — it only has to tell two links apart
 * and give back nothing usable.
 */
function fingerprint(value: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i)
    h1 = Math.imul(h1 ^ code, 2654435761)
    h2 = Math.imul(h2 ^ code, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(16)
}

function hasAnswered(token: string): boolean {
  try {
    return document.cookie.split('; ').some((entry) => entry.startsWith(`${doneCookie(token)}=`))
  } catch {
    return false
  }
}

function rememberAnswered(token: string, expiresAt: string) {
  try {
    const expires = new Date(`${expiresAt.replace(' ', 'T')}Z`).toUTCString()
    const secure = window.location.protocol === 'https:' ? '; Secure' : ''
    document.cookie = `${doneCookie(token)}=1; expires=${expires}; path=/; SameSite=Lax${secure}`
  } catch {
    /* cookies blocked: the page still works, it just cannot remember */
  }
}

export default function PublicSurvey() {
  const { t, i18n } = useTranslation()
  const { token = '' } = useParams<{ token: string }>()

  const [survey, setSurvey] = useState<PublicSurveyView | null>(null)
  const [loading, setLoading] = useState(true)
  const [answers, setAnswers] = useState<Record<string, AnswerValue>>({})
  const [name, setName] = useState('')
  const [accepted, setAccepted] = useState(false)
  const [proof, setProof] = useState<string | null>(null)
  const [showErrors, setShowErrors] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [alreadyAnswered, setAlreadyAnswered] = useState(() => hasAnswered(token))

  const widgetRef = useRef<HTMLDivElement | null>(null)
  const widgetId = useRef<string | undefined>(undefined)

  // The link is in this page's address and is the credential: nothing the page
  // loads from elsewhere (the anti-bot script) should be told it, not even by the
  // Referer header.
  useNoReferrer()

  useEffect(() => {
    api
      .get<{ data: PublicSurveyView }>(`/api/public/surveys/${token}`)
      .then((response) => {
        setSurvey(response.data)
        // A table starts from the rows its author wrote, and is sent as such.
        setAnswers(withSeedRows(response.data.questions, {}))
      })
      .catch(() => setSurvey(null))
      .finally(() => setLoading(false))
  }, [token])

  // The anti-bot widget, drawn once the page knows which site key to use. A link
  // an admin switched on without the proof has no key, and draws nothing.
  const siteKey = survey?.turnstile_site_key ?? null

  useEffect(() => {
    if (!survey || !siteKey || done || alreadyAnswered) return

    let cancelled = false

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !widgetRef.current || widgetId.current !== undefined) return
        widgetId.current = turnstile.render(widgetRef.current, {
          sitekey: siteKey,
          callback: (value) => setProof(value),
          'expired-callback': () => setProof(null),
          'error-callback': () => setProof(null),
        })
      })
      .catch(() => setError(t('publicSurvey.antibotUnavailable')))

    return () => {
      cancelled = true
    }
  }, [survey, siteKey, done, alreadyAnswered, t])

  const errors = useMemo(() => {
    if (!survey) return {}
    return Object.fromEntries(
      validateSubmission(survey.questions, answers).map((entry) => [
        entry.question_id,
        t(`survey.errors.${entry.code}`, { ...entry.params }),
      ]),
    )
  }, [survey, answers, t])

  const submit = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (!survey) return

      setShowErrors(true)
      setError(null)

      if (Object.keys(errors).length > 0) {
        setError(t('survey.fixErrors'))
        return
      }
      if (survey.collects_name && !name.trim()) {
        setError(t('publicSurvey.nameRequired'))
        return
      }
      if (!accepted) {
        setError(t('publicSurvey.acceptRequired'))
        return
      }
      if (siteKey && !proof) {
        setError(t('publicSurvey.antibotRequired'))
        return
      }

      setSubmitting(true)
      try {
        await api.post(`/api/public/surveys/${token}/submit`, {
          name: survey.collects_name ? name : undefined,
          answers,
          privacy_accepted: true,
          ...(siteKey ? { turnstile_token: proof } : {}),
        })
        rememberAnswered(token, survey.expires_at)
        setDone(true)
      } catch (err) {
        if (err instanceof ApiError && err.code === 'antibot_failed') {
          // A proof is good once: a failed one needs a new challenge.
          setProof(null)
          window.turnstile?.reset(widgetId.current)
          setError(t('publicSurvey.antibotFailed'))
        } else if (err instanceof ApiError && err.status === 404) {
          setSurvey(null)
        } else {
          setError(toErrorMessage(err) ?? t('errors.generic'))
        }
      } finally {
        setSubmitting(false)
      }
    },
    [survey, siteKey, errors, name, accepted, proof, answers, token, t],
  )

  return (
    <main
      className={cx(
        'mx-auto px-4 py-8 sm:py-12',
        survey?.questions.some((question) => question.type === 'table') ? 'max-w-6xl' : 'max-w-2xl',
      )}
    >
      {loading ? (
        <SectionSpinner />
      ) : !survey ? (
        <Card>
          <EmptyState
            title={t('publicSurvey.invalidTitle')}
            description={t('publicSurvey.invalidDescription')}
          />
        </Card>
      ) : done ? (
        <Card>
          <EmptyState
            title={t('publicSurvey.thanksTitle')}
            description={t('publicSurvey.thanksDescription')}
          />
        </Card>
      ) : alreadyAnswered ? (
        <Card className="space-y-4 p-6 text-center">
          <EmptyState
            title={t('publicSurvey.alreadyTitle')}
            description={t('publicSurvey.alreadyDescription')}
          />
          <Button variant="secondary" onClick={() => setAlreadyAnswered(false)}>
            {t('publicSurvey.answerAgain')}
          </Button>
        </Card>
      ) : (
        <form onSubmit={submit} noValidate>
          <header className="mb-6">
            <h1 className="text-display-sm text-gray-950 dark:text-white">{survey.title}</h1>
            {survey.description && (
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{survey.description}</p>
            )}
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {t('publicSurvey.openUntil', { date: formatDate(survey.expires_at.slice(0, 10), i18n.language) })}
            </p>
          </header>

          <Alert variant="info" className="mb-6">
            {t(survey.anonymous ? 'publicSurvey.anonymousNote' : 'publicSurvey.namedNote')}
          </Alert>

          {survey.collects_name && (
            <Card className="mb-6 p-6">
              <Field label={t('publicSurvey.name')} hint={t('publicSurvey.nameHint')} required>
                <Input
                  value={name}
                  maxLength={PUBLIC_LINK_LIMITS.maxNameLength}
                  autoComplete="name"
                  onChange={(event) => setName(event.target.value)}
                />
              </Field>
            </Card>
          )}

          <QuestionList
            questions={survey.questions}
            answers={answers}
            onChange={(questionId, value) =>
              setAnswers((current) => ({ ...current, [questionId]: value }))
            }
            errors={showErrors ? errors : undefined}
          />

          <Card className="mt-6 space-y-4 p-6">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-50">
              {t('publicSurvey.privacyTitle')}
            </h2>
            {/* The notice is plain text written by whoever runs the survey: shown
                as text, never as markup. */}
            <p className="max-h-48 overflow-y-auto whitespace-pre-wrap text-sm text-gray-600 dark:text-gray-300">
              {survey.privacy_notice}
            </p>
            <label className="flex items-start gap-3 text-sm">
              <input
                type="checkbox"
                checked={accepted}
                onChange={(event) => setAccepted(event.target.checked)}
                className="mt-0.5 size-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-gray-900 dark:text-gray-50">{t('publicSurvey.accept')}</span>
            </label>
          </Card>

          {/* Only for a link that asks for the proof: without it there is nothing to draw. */}
          {siteKey && <div ref={widgetRef} className="mt-6" />}

          {error && <Alert className="mt-6">{error}</Alert>}

          <div className="mt-6 flex justify-end">
            <Button type="submit" loading={submitting}>
              {t('survey.submit')}
            </Button>
          </div>
        </form>
      )}
    </main>
  )
}
